import { useCallback, useEffect } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { RawMediaCropInput, UploaderMethodProps } from 'src/app/shared/components/imageUpload';
import { useImageUploadContext } from 'src/app/shared/hooks/imageUpload';
import { useUploader } from 'src/app/shared/hooks/uppyUploader';

export type UseEndorsementImageUploadResult = {
	data: {
		image: string | undefined;
		imageCrop: RawMediaCropInput | undefined;
	};
	actions: {
		onImageChange: (uploadedImage?: string) => void;
		onImageCrop: (img?: RawMediaCropInput) => void;
	};
	uploader: UploaderMethodProps;
};

export function useEndorsementImageUpload(): UseEndorsementImageUploadResult {
	const {
		state: { image, imageCrop },
		dispatch,
	} = useImageUploadContext();
	const utilityContext = useUtilityContext();

	const handleError = useCallback(
		(errorMessage: TranslationKey) => {
			dispatch({ type: 'SET_ERROR', payload: { errorMessage } });
		},
		[dispatch],
	);

	const uploader = useUploader(handleError);

	const onImageChange = useCallback(
		(uploadedImage?: string) => {
			dispatch({ type: 'SET_IMAGE', payload: { image: uploadedImage, uploader } });
		},
		[dispatch, uploader],
	);

	const onImageCrop = useCallback(
		(img?: RawMediaCropInput) => dispatch({ type: 'SET_IMAGE_CROP', payload: { img } }),
		[dispatch],
	);

	useEffect(() => {
		uploader.onFileAdded(onImageChange);
		uploader.onUseDropTarget();
		uploader.onUseAwsS3(utilityContext, 'USER_UPLOADS');

		// No need to pass uploader in dependency array - it's stable from useMemo
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [utilityContext]);

	return {
		data: {
			image,
			imageCrop,
		},
		actions: {
			onImageChange,
			onImageCrop,
		},
		uploader,
	};
}
